import type { CustomFieldEntry, NodeType } from './node';

export const AI_ASSIST_CONFIG_SCHEMA_VERSION = '1.0.0';
export const PROMPT_CONTRACT_SCHEMA_VERSION = '1.0.0';
export const CREATE_NODE_PROMPT_CONTRACT_SCHEMA_VERSION = '1.0.0';
export const DEFAULT_HUMAN_ALIAS_PREFIX = 'Person';
export const MASKED_BUNDLE_PLACEHOLDER = '{MASKED_BUNDLE}';
export const USER_PROMPT_PLACEHOLDER = '{{USER_PROMPT}}';

export type DeIdentificationCandidateType = 'human-name' | 'field-text' | 'tag';
export type DeIdentificationSortBucket = 'human' | 'field' | 'tag';
export type DeIdentificationRuleSourceType = 'human-default' | 'candidate-override' | 'manual';
export type DeIdentificationRuleConflictStatus = 'valid' | 'blocked-conflict';
export type PromptPreviewStatus = 'idle' | 'generated' | 'copied' | 'stale';
export type SelectedBundleStalenessStatus = 'fresh' | 'stale';
export type AIResponseDraftValidationStatus = 'empty' | 'invalid-json' | 'invalid-contract' | 'valid';

export interface CandidateOccurrence {
  nodeId: string;
  fieldPath: string;
  displayLabel: string;
}

export interface DeIdentificationCandidate {
  candidateId: string;
  candidateType: DeIdentificationCandidateType;
  originalValue: string;
  replacementValue: string | null;
  defaultReplacementValue: string | null;
  enabled: boolean;
  isCustomized: boolean;
  sortBucket: DeIdentificationSortBucket;
  sortKey: string;
  occurrences: CandidateOccurrence[];
  hasDanglingOccurrences?: boolean;
}

export interface DeIdentificationRule {
  ruleId: string;
  sourceType: DeIdentificationRuleSourceType;
  originalValue: string;
  replacementValue: string;
  enabled: boolean;
  conflictStatus: DeIdentificationRuleConflictStatus;
  candidateId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DeIdentificationSettings {
  enabled: boolean;
  humanAliasPrefix: string;
  candidates: DeIdentificationCandidate[];
  manualRules: DeIdentificationRule[];
  updatedAt: string;
}

export interface AIResponseOperation {
  action: 'create' | 'update';
  nodeType: NodeType;
  nodeId?: string;
  fields: Record<string, unknown>;
}

export interface ExampleResponseEnvelope {
  operations: AIResponseOperation[];
}

interface CreateNodeDraftBase {
  displayName: string;
  summary?: string;
  tags?: string[];
  notes?: string;
  customFields?: CustomFieldEntry[];
}

export interface CreateHumanNodeDraft extends CreateNodeDraftBase {
  nodeType: 'Human';
  occupationIds?: string[];
  projectIds?: string[];
  relatedHumanIds?: string[];
}

export interface CreateProjectNodeDraft extends CreateNodeDraftBase {
  nodeType: 'Project';
  participantHumanIds?: string[];
  relatedOccupationIds?: string[];
  relatedProjectIds?: string[];
}

export interface CreateOccupationNodeDraft extends CreateNodeDraftBase {
  nodeType: 'Occupation';
  relatedHumanIds?: string[];
  typicalProjectIds?: string[];
}

export type CreateNodeDraft = CreateHumanNodeDraft | CreateProjectNodeDraft | CreateOccupationNodeDraft;

export interface PromptContractConfig {
  schemaVersion: string;
  systemPromptTemplate: string;
  inputDescription: string;
  outputDescription: string;
  responseEnvelope: 'operations';
  exampleResponse: ExampleResponseEnvelope;
}

export interface CreateNodePromptContractConfig {
  schemaVersion: string;
  systemPromptTemplate: string;
  inputDescription: string;
  outputDescription: string;
  examplePayload: CreateNodeDraft;
}

export interface PromptDiffEntry {
  diffId: string;
  beforeText: string;
  afterText: string;
  changeType: 'masked';
  displayColorToken: string;
}

export interface SelectedNodeBundle {
  selectedNodeIds: string[];
  sourceJson: string;
  maskedJson: string;
  generatedAt: string;
  stalenessStatus: SelectedBundleStalenessStatus;
}

export interface PromptPreview {
  renderedPrompt: string;
  diffEntries: PromptDiffEntry[];
  copiedAt: string | null;
  status: PromptPreviewStatus;
}

export interface AIResponseDraft {
  rawText: string;
  parsedEnvelope: ExampleResponseEnvelope | null;
  validationStatus: AIResponseDraftValidationStatus;
  issues: string[];
  validatedAt: string | null;
}

export interface CreateNodeResponseDraft {
  rawText: string;
  parsedPayload: CreateNodeDraft | null;
  validationStatus: AIResponseDraftValidationStatus;
  issues: string[];
  validatedAt: string | null;
}

export interface PreviewTextSample {
  inputText: string;
  maskedText: string | null;
  restoredText: string | null;
  issues: string[];
}

export interface GraphAIAssistConfig {
  schemaVersion: string;
  deIdentification: DeIdentificationSettings;
  promptContract: PromptContractConfig;
  createNodePromptContract: CreateNodePromptContractConfig;
}

export function createDefaultPromptContract(): PromptContractConfig {
  return {
    schemaVersion: PROMPT_CONTRACT_SCHEMA_VERSION,
    systemPromptTemplate: [
      'Return JSON only.',
      'You will receive masked node bundle JSON from JourneyNodes.',
      `Insert your work into a single object with an "operations" array.`,
      '',
      'Masked bundle:',
      MASKED_BUNDLE_PLACEHOLDER,
    ].join('\n'),
    inputDescription: 'You will receive masked node bundle JSON generated from the selected JourneyNodes graph nodes.',
    outputDescription: 'Return a single JSON object with an operations array. Use action=create for new nodes and action=update with nodeId for existing nodes.',
    responseEnvelope: 'operations',
    exampleResponse: {
      operations: [
        {
          action: 'create',
          nodeType: 'Project',
          fields: {
            displayName: 'Masked Collaboration Initiative',
            summary: 'A project created from masked input.',
            tags: ['planning'],
            notes: 'Created from the JourneyNodes AI workspace.',
            customFields: [
              {
                fieldId: 'project-scope',
                label: 'Project Scope',
                value: 'Pilot rollout',
                valueType: 'text',
                displayOrder: 0,
              } satisfies CustomFieldEntry,
            ],
            participantHumanIds: [],
            relatedOccupationIds: [],
            relatedProjectIds: [],
          },
        },
        {
          action: 'update',
          nodeType: 'Human',
          nodeId: 'human-alpha',
          fields: {
            summary: 'Updated from the masked input context.',
            tags: ['mentor'],
            customFields: [
              {
                fieldId: 'preferred-contact-window',
                label: 'Preferred Contact Window',
                value: 'Weekday afternoons',
                valueType: 'text',
                displayOrder: 0,
              } satisfies CustomFieldEntry,
            ],
          },
        },
      ],
    },
  };
}

export function createDefaultCreateNodePromptContract(): CreateNodePromptContractConfig {
  return {
    schemaVersion: CREATE_NODE_PROMPT_CONTRACT_SCHEMA_VERSION,
    systemPromptTemplate: [
      'Return JSON only.',
      'Create exactly one JourneyNodes node that satisfies the user request.',
      'Do not wrap the response in Markdown fences or prose.',
      'Choose nodeType from Human, Project, or Occupation.',
      'Do not include nodeId in the response.',
      'Follow the allowed field list and example JSON payload below.',
      '',
      'User request:',
      USER_PROMPT_PLACEHOLDER,
    ].join('\n'),
    inputDescription: 'You will receive a natural-language request describing one new JourneyNodes node to create.',
    outputDescription: 'Return a single JSON object for one new node. Include nodeType and displayName, then only the fields allowed for that node type. Relation arrays may reference existing nodes by nodeId or exact unique displayName.',
    examplePayload: {
      nodeType: 'Project',
      displayName: 'Masked Collaboration Initiative',
      summary: 'A project created from a natural-language request.',
      tags: ['planning', 'ai-assisted'],
      notes: 'Created from the JourneyNodes AI create-node workspace.',
      customFields: [
        {
          fieldId: 'project-scope',
          label: 'Project Scope',
          value: 'Pilot rollout for a small internal group',
          valueType: 'text',
          displayOrder: 0,
        } satisfies CustomFieldEntry,
      ],
      participantHumanIds: ['human-alpha'],
      relatedOccupationIds: ['occupation-designer'],
      relatedProjectIds: [],
    },
  };
}

export function createEmptyResponseDraft(): AIResponseDraft {
  return {
    rawText: '',
    parsedEnvelope: null,
    validationStatus: 'empty',
    issues: [],
    validatedAt: null,
  };
}

export function createEmptyCreateNodeResponseDraft(): CreateNodeResponseDraft {
  return {
    rawText: '',
    parsedPayload: null,
    validationStatus: 'empty',
    issues: [],
    validatedAt: null,
  };
}

export function createEmptyPromptPreview(): PromptPreview {
  return {
    renderedPrompt: '',
    diffEntries: [],
    copiedAt: null,
    status: 'idle',
  };
}

export function createEmptyPreviewTextSample(): PreviewTextSample {
  return {
    inputText: '',
    maskedText: null,
    restoredText: null,
    issues: [],
  };
}

export function createDefaultGraphAIAssistConfig(now = new Date().toISOString()): GraphAIAssistConfig {
  return {
    schemaVersion: AI_ASSIST_CONFIG_SCHEMA_VERSION,
    deIdentification: {
      enabled: true,
      humanAliasPrefix: DEFAULT_HUMAN_ALIAS_PREFIX,
      candidates: [],
      manualRules: [],
      updatedAt: now,
    },
    promptContract: createDefaultPromptContract(),
    createNodePromptContract: createDefaultCreateNodePromptContract(),
  };
}

export function cloneGraphAIAssistConfig(config: GraphAIAssistConfig): GraphAIAssistConfig {
  return JSON.parse(JSON.stringify(config)) as GraphAIAssistConfig;
}
